/**
 * `overlay_prompt` renderer — composes `@nextday/funnel-ui` `OverlayPrompt`.
 *
 * Displays a modal/prompt/banner overlay on top of the previous step.
 * Supports multiple placements (center, bottom-sheet, top-banner), hero
 * variants (image-fan, single-image, icon), tones, countdown timers,
 * gate progress, and various action layouts.
 *
 * Stores the selected action id under `data.answerKey`.
 *
 * Step JSON shape:
 *
 *   {
 *     "type": "overlay_prompt",
 *     "data": {
 *       "answerKey": "overlay_choice",
 *       "title": "Do you want her to send photos?",
 *       "actions": [
 *         { "id": "yes", "label": "Yes", "variant": "primary" },
 *         { "id": "no",  "label": "No",  "variant": "secondary" }
 *       ]
 *     }
 *   }
 */
import {
  DynamicIcon,
  OverlayPrompt,
  type OverlayAction,
  type OverlayHero,
} from "@nextday/funnel-ui";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

const heroImageSchema = z.object({
  imageUrl: z.string().min(1),
  alt: z.string().optional(),
});

const accentBadgeSchema = z.object({
  label: z.string().min(1),
  tone: z.enum(["default", "success", "warning", "danger"]).optional(),
});

const heroSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("image-fan"),
    images: z.array(heroImageSchema).min(1).max(3),
    accentBadge: accentBadgeSchema.optional(),
  }),
  z.object({
    kind: z.literal("single-image"),
    image: heroImageSchema,
    accentBadge: accentBadgeSchema.optional(),
  }),
  z.object({
    kind: z.literal("icon"),
    iconName: z.string().min(1),
    iconTone: z.enum(["default", "success", "warning", "danger", "primary"]).optional(),
    accentBadge: accentBadgeSchema.optional(),
  }),
  z.object({ kind: z.literal("none") }),
]);

const actionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  variant: z.enum(["primary", "secondary", "ghost", "danger"]).optional(),
  iconName: z.string().optional(),
  autoFocus: z.boolean().optional(),
});

const countdownSchema = z.object({
  seconds: z.number().int().positive(),
  label: z.string().optional(),
});

const gateSchema = z.object({
  progress: z.number().min(0).max(1),
  label: z.string().optional(),
});

export const overlayPromptSchema = z
  .object({
    answerKey: z.string().min(1),
    placement: z.enum(["center", "bottom-sheet", "top-banner"]).optional(),
    size: z.enum(["sm", "md", "lg"]).optional(),
    tone: z.enum(["default", "celebratory", "warning", "danger"]).optional(),
    hero: heroSchema.optional(),
    eyebrow: z.string().optional(),
    title: z.string().min(1),
    description: z.string().optional(),
    footnote: z.string().optional(),
    caption: z.string().optional(),
    actions: z.array(actionSchema).min(1),
    actionLayout: z.enum(["stack", "row"]).optional(),
    dismissible: z.boolean().optional(),
    countdown: countdownSchema.optional(),
    gate: gateSchema.optional(),
    role: z.enum(["dialog", "alertdialog"]).optional(),
  })
  .strict();

export type OverlayPromptData = z.infer<typeof overlayPromptSchema>;

const resolveIcon = (iconName: string): React.ReactNode => (
  <DynamicIcon icon={iconName} size={32} />
);

const resolveHero = (hero: z.infer<typeof heroSchema>): OverlayHero => {
  switch (hero.kind) {
    case "image-fan":
      return { kind: "image-fan", images: hero.images, accentBadge: hero.accentBadge };
    case "single-image":
      return { kind: "single-image", image: hero.image, accentBadge: hero.accentBadge };
    case "icon":
      return {
        kind: "icon",
        icon: resolveIcon(hero.iconName),
        iconTone: hero.iconTone,
        accentBadge: hero.accentBadge,
      };
    case "none":
      return { kind: "none" };
  }
};

const OverlayPromptRenderer = ({
  data,
  step,
  onAnswer,
  onAdvance,
}: RendererProps<OverlayPromptData>) => {
  const [open, setOpen] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const hero = useMemo(() => (data.hero ? resolveHero(data.hero) : undefined), [data.hero]);

  const handleAction = useCallback(
    (actionId: string) => {
      setActionLoading(actionId);
      onAnswer(data.answerKey, { value: actionId });
      setOpen(false);
      onAdvance();
    },
    [data.answerKey, onAnswer, onAdvance],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    onAnswer(data.answerKey, { value: "__dismissed" });
    onAdvance();
  }, [data.answerKey, onAnswer, onAdvance]);

  const resolvedActions: OverlayAction[] = useMemo(
    () =>
      data.actions.map((a) => ({
        ...a,
        startContent: a.iconName ? <DynamicIcon icon={a.iconName} size={20} /> : undefined,
        isLoading: actionLoading === a.id,
        onPress: () => handleAction(a.id),
      })),
    [data.actions, actionLoading, handleAction],
  );

  return (
    <OverlayPrompt
      open={open}
      placement={data.placement}
      size={data.size}
      tone={data.tone}
      hero={hero}
      eyebrow={data.eyebrow}
      title={data.title}
      description={data.description}
      footnote={data.footnote}
      caption={data.caption}
      actions={resolvedActions}
      actionLayout={data.actionLayout}
      dismissible={data.dismissible}
      countdown={data.countdown}
      gate={data.gate}
      role={data.role}
      error={null}
      onClose={data.dismissible ? handleClose : undefined}
      testId={`renderer-overlay-prompt-${step.id}`}
    />
  );
};

export const overlayPromptRenderer = createRenderer({
  type: "overlay_prompt",
  dataSchema: overlayPromptSchema,
  label: "Overlay prompt",
  Component: OverlayPromptRenderer,
});

export default overlayPromptRenderer;
